import { Grid, InputAdornment, TextField } from "@mui/material";
import genericStyles from "../../Styles/genericStyles.module.css";
import { Controller, useForm } from "react-hook-form";
import { Vocabulary } from "../../Utils/Vocabulary";
import { AccountSchema } from "../../Utils/Models";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormsIds } from "../../Utils/Constants";
import { isDefined, onFormError } from "../../Utils/Utils";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { AccountsTableProps } from "../../Utils/Types";
import { useEffect } from "react";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponents";
import { updateData } from "../../Services/updateData";

export default function AccountForm(props: AccountsTableProps) {
  const { onClose, contextValue } = props;

  // Initialize the form with Yup validation and default values
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(AccountSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   *
   */
  useEffect(() => {
    if (isDefined(contextValue)) reset(contextValue);
  }, []);

  const updateElement = async (model: any) => {
    const url = `${endpoints.accounts}/${contextValue?._id}`;

    try {
      const res = (await updateData(url, model)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      onClose();
    } catch (error) {
      console.error("Error updating vehicle:", error);
    }
  };

  /**
   * Called when the form is submitted.
   *
   * Creates a new account based on the form data. If the creation is successful,
   * it reloads the accounts table by setting the `triggerReload` state to its
   * negation.
   *
   * @param {Object} data The form data as per the `AccountSchema`.
   */
  const createElement = async (data: any) => {
    const url = `${endpoints.accounts}`;

    try {
      const res = (await postData(url, data)) as any;
      onClose();

      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
    } catch (error) {
      console.error("Error creating class:", error);
    }
  };

  /**
   * Handles form submission. Depending on the presence of an `_id` in the `contextValue`,
   * it either updates an existing account or creates a new one using the provided `model`.
   *
   * @param {Object} model - The form data to be processed.
   */

  const onSubmit = (model: any) => {
    contextValue._id ? updateElement(model) : createElement(model);
  };

  /**
   *
   */
  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      id={FormsIds.accountForm}
    >
      <Grid className={genericStyles.formContainer} container spacing={5}>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={"name"}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value}
                id="name"
                name="name"
                label={Vocabulary.name}
                variant="outlined"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={"iban"}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value}
                id="iban"
                name="iban"
                label={Vocabulary.iban}
                variant="outlined"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        {contextValue?._id && (
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name={"balance"}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value}
                  id="balance"
                  name="balance"
                  label={Vocabulary.value}
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                    endAdornment: (
                      <InputAdornment position="end">
                        {Vocabulary.currency}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
}
